import { Download, RefreshCw } from 'lucide-react';
import type { AndroidUpdateOffer } from '../../core/config/androidUpdate';
import type { AndroidUpdateUiPhase } from '../../hooks/useAndroidAppUpdate';
import s from './AndroidUpdatePrompt.module.scss';

export function AndroidUpdatePrompt({
  t,
  offer,
  phase,
  force,
  errorKey,
  onUpdate,
  onLater,
}: {
  t: Record<string, string>;
  offer: AndroidUpdateOffer;
  phase: AndroidUpdateUiPhase;
  force: boolean;
  errorKey: string | null;
  onUpdate: () => void;
  onLater: () => void;
}) {
  const busy = phase === 'preparing' || phase === 'downloading' || phase === 'installing';
  const statusText = phase === 'downloading'
    ? t.appUpdateDownloading
    : phase === 'installing' || phase === 'preparing'
      ? t.appUpdateInstalling
      : null;

  const errorText = errorKey === 'install_permission_required'
    ? t.appUpdatePermissionNeeded
    : errorKey
      ? t.appUpdateFailed
      : null;

  return (
    <div className={s.root} role="dialog" aria-modal="true" aria-labelledby="android-update-title">
      <div className={s.card}>
        <div className={s.badge}>{t.appUpdateBadge}</div>
        <h2 id="android-update-title" className={s.title}>
          {force ? t.appUpdateRequiredTitle : t.appUpdateAvailableTitle}
        </h2>
        <p className={s.body}>
          {force ? t.appUpdateRequiredBody : t.appUpdateAvailableBody}
        </p>
        <div className={s.versions}>
          {t.appUpdateVersions
            .replace('{current}', offer.installed.versionName)
            .replace('{latest}', offer.remote.versionName)}
        </div>
        {offer.remote.notes ? <p className={s.notes}>{offer.remote.notes}</p> : null}
        {statusText ? (
          <p className={s.status} aria-live="polite">
            <RefreshCw size={14} className={s.spin} aria-hidden />
            {statusText}
          </p>
        ) : null}
        {errorText ? <p className={s.error}>{errorText}</p> : null}
        <div className={s.actions}>
          {!force && (
            <button
              type="button"
              className={`tap ${s.later}`}
              onClick={onLater}
              disabled={busy}
            >
              {t.appUpdateLater}
            </button>
          )}
          <button
            type="button"
            className={`tap ${s.update}`}
            onClick={onUpdate}
            disabled={busy}
            aria-busy={busy}
          >
            <Download size={16} strokeWidth={1.7} aria-hidden />
            {busy ? t.appUpdateUpdating : t.appUpdateAction}
          </button>
        </div>
      </div>
    </div>
  );
}
